import { defineStore } from "pinia";
import { getConfigurationHubObjectParsed } from "~/helpers";
import type { ITerm } from "~/types";
import { transformText, type ITermsOptions } from "~/helpers/transformText";

interface ITerminology {
  terms: ITerm[];
  article: string;
  articles: string;
  associateEditors: string;
  author: string;
  authors: string;
  callForPapers: string;
  ebook: string;
  editorialOffice: string;
  fieldChiefEditor: string;
  fieldChiefEditors: string;
  researchTopic: string;
  researchTopics: string;
  reviewEditors: string;
  specialtyChiefEditor: string;
  specialtyChiefEditors: string;
  tenantName: string;
  topicTitle: string;
  researchTopicEditorTitle: string;
}
export type TerminologyKeys = keyof ITerminology;

export const useTerminologyStore = defineStore("terminology", {
  state: (): ITerminology => ({
    terms: [],
    article: "",
    articles: "",
    associateEditors: "",
    author: "",
    authors: "",
    callForPapers: "",
    ebook: "",
    editorialOffice: "",
    fieldChiefEditor: "",
    fieldChiefEditors: "",
    researchTopic: "",
    researchTopics: "",
    reviewEditors: "",
    specialtyChiefEditor: "",
    specialtyChiefEditors: "",
    tenantName: "",
    topicTitle: "",
    researchTopicEditorTitle: ""
  }),
  getters: {
    getTerm:
      (state: ITerminology) =>
      (key: TerminologyKeys): string => {
        if (key in state) {
          return state[key] as string;
        } else {
          throw new Error(`Property ${key} does not exist on state.`);
        }
      },
    getOriginalTerm:
      (state: ITerminology) =>
      (
        keyTerm: string,
        defaultValue: string,
        options: ITermsOptions = {}
      ): string => {
        const term = state.terms.find((f) => f.key === keyTerm);
        let tenantTerm = "";
        if (term) {
          tenantTerm = term.tenantTerm
            ? term.tenantTerm
            : term.frontiersDefaultTerm;
        } else if (defaultValue) {
          tenantTerm = defaultValue;
        }
        return transformText(tenantTerm, options);
      }
  },
  actions: {
    init() {
      const { terminologySettings, isCallForPapersVisible } =
        useRuntimeConfig().public;
      try {
        const settings = getConfigurationHubObjectParsed<{ terms: ITerm[] }>(
          terminologySettings
        );
        this.terms = settings?.terms || [];
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : err;
        throw new Error(
          "Error trying to parse terminology settings. " + errorMessage
        );
      }

      this.article = this.getOriginalTerm("article", "article", {
        firstCharUppercase: true
      });
      this.articles = this.getOriginalTerm("articles", "articles", {
        firstCharUppercase: true
      });
      this.associateEditors = this.getOriginalTerm(
        "associate_editors",
        "associate editors",
        {
          firstCharUppercase: true
        }
      );
      this.author = this.getOriginalTerm("author", "author", {
        firstCharUppercase: true
      });
      this.authors = this.getOriginalTerm("authors", "authors", {
        firstCharUppercase: true
      });
      isCallForPapersVisible &&
        (this.callForPapers = this.getOriginalTerm(
          "call_for_papers",
          "call for papers",
          {
            capitalLetters: true
          }
        ));
      this.ebook = this.getOriginalTerm("e_book", "ebook", {
        firstCharUppercase: true
      });
      this.editorialOffice = this.getOriginalTerm(
        "editorial_office",
        "editorial office",
        {
          firstCharUppercase: true
        }
      );
      this.fieldChiefEditor = this.getOriginalTerm(
        "field_chief_editor",
        "field chief editor",
        {
          firstCharUppercase: true
        }
      );
      this.fieldChiefEditors = this.getOriginalTerm(
        "field_chief_editors",
        "field chief editors",
        {
          firstCharUppercase: true
        }
      );
      this.researchTopic = this.getOriginalTerm(
        "research_topic",
        "research topic",
        {
          capitalLetters: true
        }
      );
      this.researchTopics = this.getOriginalTerm(
        "research_topics",
        "research topics",
        {
          capitalLetters: true
        }
      );
      this.reviewEditors = this.getOriginalTerm(
        "review_editors",
        "review editors",
        {
          firstCharUppercase: true
        }
      );
      this.specialtyChiefEditor = this.getOriginalTerm(
        "specialty_chief_editor",
        "specialty chief editor",
        {
          firstCharUppercase: true
        }
      );
      this.specialtyChiefEditors = this.getOriginalTerm(
        "specialty_chief_editors",
        "specialty chief editors",
        {
          firstCharUppercase: true
        }
      );
      this.tenantName = this.getOriginalTerm("frontiers", "frontiers", {
        firstCharUppercase: true
      });
      this.topicTitle = this.getOriginalTerm("topic", "topic", {
        firstCharUppercase: true
      });
      this.researchTopicEditorTitle = this.getOriginalTerm(
        "topic_editors",
        "topic editors",
        {
          firstCharUppercase: true
        }
      );
    }
  }
});
